import { toastError } from '@/lib/toast-error';
import { trpc } from '@/lib/trpc';
import type {
  CommunityMemberFilters,
} from '../../../shared/types';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * Hook to get all community members with optional filters
 * ✨ Migrated to tRPC with full type safety
 */
export function useCommunityMembers(filters?: CommunityMemberFilters) {
  const result = trpc.community.getMembers.useQuery(filters || {});

  return {
    data: result.data?.data || [],
    isLoading: result.isLoading,
    error: result.error,
  };
}

/**
 * Hook to get a single community member by ID
 * ✨ Migrated to tRPC with full type safety
 */
export function useCommunityMember(memberId: string) {
  return trpc.community.getMember.useQuery(memberId, {
    enabled: !!memberId,
  });
}

/**
 * Hook to create a new community member
 * ✨ Migrated to tRPC with full type safety
 */
export function useCreateCommunityMember() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.community.createMember.useMutation({
    onSuccess: () => {
      utils.community.getMembers.invalidate();
      toast.success(t('hooks.communitymap.miembro_agregado_exitosamente'));
    },
    onError: (error) => {
      console.error('Error creating community member:', error);
      toastError(error, 'common.toast.error_al_crear_miembro');
    },
  });
}

/**
 * Hook to update an existing community member
 * ✨ Migrated to tRPC with full type safety
 */
export function useUpdateCommunityMember() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.community.updateMember.useMutation({
    onSuccess: (_, variables) => {
      utils.community.getMembers.invalidate();
      utils.community.getMember.invalidate(variables.memberId);
      toast.success(t('hooks.communitymap.miembro_actualizado_exitosamente'));
    },
    onError: (error) => {
      console.error('Error updating community member:', error);
      toastError(error, 'common.toast.error_al_actualizar_miembro');
    },
  });
}

/**
 * Hook to delete a community member (soft delete - sets status to Baja)
 * ✨ Migrated to tRPC with full type safety
 */
export function useDeleteCommunityMember() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  return trpc.community.deleteMember.useMutation({
    onSuccess: () => {
      utils.community.getMembers.invalidate();
      toast.success(t('hooks.communitymap.miembro_marcado_como_baja'));
    },
    onError: (error) => {
      console.error('Error deleting community member:', error);
      toastError(error, 'common.toast.error_al_eliminar_miembro');
    },
  });
}

/**
 * Hook to sync community map from Google Sheets
 */
export function useSyncCommunityMap() {
  const utils = trpc.useUtils();

  return trpc.community.syncFromSheets.useMutation({
    onSuccess: (result) => {
      utils.community.getMembers.invalidate();
      const parts = [
        `${result.created} creados`,
        `${result.updated} actualizados`,
      ];
      if (result.deactivated > 0) parts.push(`${result.deactivated} dados de baja`);
      if (result.errors > 0) parts.push(`${result.errors} errores`);
      toast.success(`Sincronizado: ${parts.join(', ')}`);
    },
    onError: (error) => {
      console.error('Error syncing community map:', error);
      toastError(error, 'common.toast.error_al_sincronizar_con_sheets');
    },
  });
}

/**
 * Hook to upload community map Excel file
 * ✨ Migrated to tRPC with full type safety
 */
export function useUploadCommunityMap() {
  const utils = trpc.useUtils();

  return trpc.community.uploadCommunityMap.useMutation({
    onSuccess: (result) => {
      utils.community.getMembers.invalidate();

      const message = `
        Carga completada:
        ${result.processedCount} procesados
      `;

      toast.success(message);
    },
    onError: (error) => {
      console.error('Error uploading community map:', error);
      toastError(error, 'common.toast.error_al_cargar_community_map');
    },
  });
}
